// Enhanced toast hook with sound notifications
import { toast } from 'react-hot-toast';
import soundManager from '../utils/soundManager';

// Custom hook for toast with sound notifications
export const useToastWithSound = () => {
  // Success toast with sound
  const showSuccess = (message, options = {}) => {
    soundManager.playSuccess();
    return toast.success(message, {
      duration: 4000,
      position: 'top-right',
      style: {
        background: '#10B981',
        color: '#fff',
        fontWeight: '500',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#10B981',
      },
      ...options
    });
  };

  // Error toast with sound
  const showError = (message, options = {}) => {
    soundManager.playError();
    return toast.error(message, {
      duration: 5000,
      position: 'top-right',
      style: {
        background: '#EF4444',
        color: '#fff',
        fontWeight: '500',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#EF4444',
      },
      ...options
    });
  };

  // Warning toast with sound
  const showWarning = (message, options = {}) => {
    soundManager.playWarning();
    return toast(message, {
      duration: 4500,
      position: 'top-right',
      icon: '⚠️',
      style: {
        background: '#F59E0B',
        color: '#fff',
        fontWeight: '500',
      },
      ...options
    });
  };

  // Info toast (uses success sound)
  const showInfo = (message, options = {}) => {
    soundManager.playSuccess();
    return toast(message, {
      duration: 4000,
      position: 'top-right',
      icon: 'ℹ️',
      style: {
        background: '#3B82F6',
        color: '#fff',
        fontWeight: '500',
      },
      ...options
    });
  };

  // Loading toast (no sound)
  const showLoading = (message, options = {}) => {
    return toast.loading(message, {
      position: 'top-right',
      style: {
        background: '#6B7280',
        color: '#fff',
        fontWeight: '500',
      },
      ...options
    });
  };

  // Promise toast with sounds
  const showPromise = (promise, messages, options = {}) => {
    return toast.promise(
      promise,
      {
        loading: messages.loading || 'Loading...',
        success: (data) => {
          soundManager.playSuccess();
          return messages.success || 'Success!';
        },
        error: (err) => {
          soundManager.playError();
          return messages.error || 'Error occurred!';
        },
      },
      {
        position: 'top-right',
        success: {
          duration: 4000,
          style: {
            background: '#10B981',
            color: '#fff',
            fontWeight: '500',
          },
        },
        error: {
          duration: 5000,
          style: {
            background: '#EF4444',
            color: '#fff',
            fontWeight: '500',
          },
        },
        loading: {
          style: {
            background: '#6B7280',
            color: '#fff',
            fontWeight: '500',
          },
        },
        ...options
      }
    );
  };

  // Action-specific toasts
  const showActionToast = (action, success = true, customMessage = null) => {
    const actionMessages = {
      // Login/Logout
      login: {
        success: 'Login successful! Welcome back! 👋',
        error: 'Login failed. Please check your credentials.'
      },
      logout: {
        success: 'Logged out successfully. See you soon! 👋',
        error: 'Logout failed. Please try again.'
      },
      
      // User Management
      createUser: {
        success: 'User created successfully! 👤',
        error: 'Failed to create user. Please try again.'
      },
      updateUser: {
        success: 'User updated successfully! ✏️',
        error: 'Failed to update user. Please try again.'
      },
      deleteUser: {
        success: 'User deleted successfully! 🗑️',
        error: 'Failed to delete user. Please try again.'
      },
      
      // Service Management
      createService: {
        success: 'Service created successfully! 🛠️',
        error: 'Failed to create service. Please try again.'
      },
      updateService: {
        success: 'Service updated successfully! ✏️',
        error: 'Failed to update service. Please try again.'
      },
      deleteService: {
        success: 'Service deleted successfully! 🗑️',
        error: 'Failed to delete service. Please try again.'
      },
      
      // Sales Management
      addSale: {
        success: 'Sale recorded successfully! 💰',
        error: 'Failed to record sale. Please try again.'
      },
      updateSale: {
        success: 'Sale updated successfully! ✏️',
        error: 'Failed to update sale. Please try again.'
      },
      deleteSale: {
        success: 'Sale deleted successfully! 🗑️',
        error: 'Failed to delete sale. Please try again.'
      },
      
      // Password Management
      changePassword: {
        success: 'Password changed successfully! 🔒',
        error: 'Failed to change password. Please try again.'
      },
      
      // Session Management
      sessionWarning: {
        warning: 'Your session will expire soon! ⏰'
      },
      sessionExpired: {
        error: 'Session expired. Please login again. ⏰'
      },
      
      // Form Validation
      validationError: {
        error: 'Please fill all required fields correctly. ⚠️'
      },
      
      // Data Operations
      save: {
        success: 'Data saved successfully! 💾',
        error: 'Failed to save data. Please try again.'
      },
      delete: {
        success: 'Data deleted successfully! 🗑️',
        error: 'Failed to delete data. Please try again.'
      },
      update: {
        success: 'Data updated successfully! ✏️',
        error: 'Failed to update data. Please try again.'
      }
    };

    const messages = actionMessages[action];
    if (!messages) {
      // Fallback for unknown actions
      if (success) {
        showSuccess(customMessage || 'Operation completed successfully!');
      } else {
        showError(customMessage || 'Operation failed. Please try again.');
      }
      return;
    }

    // Use custom message if provided, otherwise use default
    const message = customMessage || (success ? messages.success : messages.error);

    // Show appropriate toast based on action and success status
    if (action === 'sessionWarning') {
      showWarning(message);
    } else if (success) {
      showSuccess(message);
    } else {
      showError(message);
    }

    // Play corresponding sound
    soundManager.playForAction(action, success);
  };

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    showPromise,
    showActionToast,
    // Expose original toast for backward compatibility
    toast
  };
};

// Export individual functions for direct use
export const showSuccessToast = (message, options) => {
  soundManager.playSuccess();
  return toast.success(message, options);
};

export const showErrorToast = (message, options) => {
  soundManager.playError();
  return toast.error(message, options);
};

export const showWarningToast = (message, options) => {
  soundManager.playWarning();
  return toast(message, { icon: '⚠️', ...options });
};

export default useToastWithSound;
